import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styled from "@emotion/styled";

const ChipButton = styled(Button, {
  shouldForwardProp: (prop) =>
    !["$active", "$hasValue", "$disabledLook", "$valueFill"].includes(prop),
})(({ theme, $active, $hasValue, $disabledLook, $valueFill }) => {
  const isDark = theme.palette.mode === "dark";
  const primary = theme.palette.primary.main;
  // Match empty Search filter chips (e.g. Revenue): thin neutral gray stroke.
  const borderIdle = isDark ? "rgba(255, 255, 255, 0.18)" : "rgba(88, 89, 91, 0.22)";
  const borderActive = primary;
  const bgIdle = isDark ? "#1E1E1E" : "#FFFFFF";
  const bgHover = isDark ? "rgba(255, 255, 255, 0.06)" : alpha(primary, 0.04);
  const bgActive = isDark ? "#2A2A2A" : "#FFFFFF";
  const bgValue = isDark ? alpha(primary, 0.24) : alpha(primary, 0.07);
  const useValueFill = $hasValue && $valueFill;
  // Outline summary chips (Filters): purple stroke only while selected/open.
  // Filled filter chips keep purple stroke whenever a value is applied.
  const emphasizedBorder = $active || ($hasValue && $valueFill);

  return {
    minHeight: 36,
    maxWidth: 240,
    padding: "6px 10px 6px 12px",
    borderRadius: 999,
    textTransform: "none",
    fontWeight: 600,
    fontSize: 12.5,
    lineHeight: 1.25,
    letterSpacing: 0,
    color: isDark ? "#FFFFFF" : "#171717",
    backgroundColor: $active ? bgActive : useValueFill ? bgValue : bgIdle,
    border: `${emphasizedBorder ? 1.5 : 1}px solid ${
      emphasizedBorder ? borderActive : borderIdle
    }`,
    boxShadow: $active
      ? isDark
        ? `0 0 0 1px ${alpha(primary, 0.75)}, 0 2px 6px rgba(0, 0, 0, 0.28)`
        : `0 0 0 0.5px ${alpha(primary, 0.4)}, 0 2px 6px rgba(23, 15, 74, 0.08)`
      : "none",
    opacity: $disabledLook ? 0.45 : 1,
    justifyContent: "space-between",
    gap: 6,
    flexShrink: 0,
    transform: $active ? "translateY(-1px)" : "none",
    transition:
      "background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease",
    "&:hover": {
      backgroundColor: $active || useValueFill ? ($active ? bgActive : bgValue) : bgHover,
      borderColor: emphasizedBorder ? borderActive : borderIdle,
      // Avoid translateY on hover — parent overflow clips the pill border curve.
      transform: $active ? "translateY(-1px)" : "none",
    },
    "&.Mui-disabled": {
      opacity: 0.45,
      color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.38)",
      borderColor: borderIdle,
      backgroundColor: bgIdle,
      transform: "none",
    },
    // Keep chevron at icon size; do not let endIcon force badge text to 20px.
    "& .MuiButton-endIcon": {
      marginLeft: 2,
      marginRight: 0,
    },
    "@media (max-width: 600px)": {
      maxWidth: 220,
      minHeight: 34,
      fontSize: 12,
      padding: "5px 8px 5px 10px",
    },
  };
});

const ChipContent = styled(Box)(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  minWidth: 0,
  maxWidth: "100%",
}));

const ChipLabel = styled(Typography)(() => ({
  fontSize: "inherit",
  fontWeight: 600,
  lineHeight: 1.25,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textAlign: "left",
}));

const CountBadge = styled(Box)(({ theme }) => ({
  minWidth: 20,
  height: 20,
  padding: "0 7px",
  borderRadius: 999,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontSize: "11px",
  fontWeight: 700,
  lineHeight: "12px",
  letterSpacing: 0,
  whiteSpace: "nowrap",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));

/**
 * Horizontal filter trigger.
 * - empty: label + chevron
 * - single/custom: truncated value only
 * - multi: label + count badge (replaces chevron)
 * - valueFill: when true (default), applied filters use a light purple wash
 * - badgeLabel: optional custom badge copy (defaults to the numeric count)
 */
export default function FilterChip({
  label,
  active = false,
  hasValue = false,
  disabled = false,
  disabledLook = false,
  count = 0,
  badgeLabel,
  variant = "empty",
  valueFill = true,
  hideChevron = false,
  onClick,
  "aria-controls": ariaControls,
  "aria-label": ariaLabel,
  id,
}) {
  const showBadge = variant === "multi" && count > 0;
  const showChevron = variant === "empty" && !hideChevron;
  const badgeText = badgeLabel || String(count);

  return (
    <ChipButton
      id={id}
      type="button"
      disableElevation
      disabled={disabled}
      $active={active}
      $hasValue={hasValue}
      $disabledLook={disabledLook}
      $valueFill={valueFill}
      onClick={onClick}
      aria-label={ariaLabel || undefined}
      aria-haspopup="dialog"
      aria-expanded={active ? "true" : "false"}
      aria-controls={ariaControls}
      {...(showChevron
        ? {
            endIcon: (
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  transform: active ? "rotate(180deg)" : "none",
                  transition: "transform 180ms ease",
                }}
              />
            ),
          }
        : {})}
    >
      <ChipContent>
        <ChipLabel component="span">{label}</ChipLabel>
        {showBadge ? (
          <CountBadge component="span" aria-hidden="true">
            {badgeText}
          </CountBadge>
        ) : null}
      </ChipContent>
    </ChipButton>
  );
}
